/* eslint-disable no-nested-ternary */
import { Button, Form, message, Modal, Select, Tooltip, Upload } from "antd";
import { ESubmittalStatus, UserParticipationType } from "constants/index";
import { TFetureDetailUpdateAction } from "entity-app/models";
import { useEffect, useMemo, useState } from "react";
import TextArea from "antd/lib/input/TextArea";
import {
  CheckCircleFilled,
  CloseCircleOutlined,
  DeleteOutlined,
  LinkOutlined,
  LoadingOutlined
} from "@ant-design/icons";
import { DateUtils } from "utils/dateutils";
import DatePickerWithIcon from "components/date-picker-with-icon/date-picker-with-icon";
import BtnAlternateAssign from "components/widgets/btn-alternate-assign";
import { CloseAndDistributeSubmittal } from "components/close-and-distribute";
import { noAuthClient } from "services/axios";
import { generateS3URL } from "services/file-mgmt";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { SUBMITTAL_WORKFLOW_RESPONSES } from "entity-app/graphQL/ciq-feature-queries";
import { FeatureTypes } from "entity-app/constants";
import { useSubmmittialDetails } from "../hook/submittal-details-hook";

interface IDocumentObject {
  file: File;
  status: number;
}

const getAssigneeActualDateStates = () => {
  return [
    {
      label: "Assigned to Subcontractor on",
      id: 1
    },
    {
      label: "Submitted by Subcontractor on",
      id: 2
    },
    {
      label: "Submitted for Design Review on",
      id: 3
    },
    {
      label: "Design Review Completed on",
      id: 4
    },
    {
      label: "Submittal Distributed on",
      id: 5
    }
  ];
};

export default function SubmittalWorkflowResponse(props: {
  actions: TFetureDetailUpdateAction;
  projectId: string;
  gqlClientForProject: any;
  submittal: any;
  editableFields: any[];
  workflowOverride: {
    isWorkflowInOverrideMode: boolean;
    setIsWorkflowInOverrideMode: Function;
  };
  isRevisionMode: boolean;
  tokenRetrievalState: any;
  updateFeatureData: {
    localFeatureInstanceData: any;
    setLocalFeatureInstanceData: Function;
  };
  submittalViewState: any;
}) {
  const {
    updateFeatureData: { localFeatureInstanceData },
    actions,
    projectId,
    submittal,
    workflowOverride,
    isRevisionMode,
    tokenRetrievalState,
    gqlClientForProject,
    submittalViewState,
    editableFields
  } = props;

  const { isWorkflowInOverrideMode, setIsWorkflowInOverrideMode } =
    workflowOverride;
  const [submittalAssignInProg, setSubmittalAssignInProg] = useState(false);
  const [isAltAssignee, setIsAltAssigne] = useState(false);

  const { Option } = Select;

  const [modal, contextHolder] = Modal.useModal();

  const [currentActualDateState, setCurrentActualDateState] = useState({
    label: "",
    id: -1,
    value: DateUtils.dateTimeObj(),
    previousDate: null as any
  });
  const [altAssignee, setAltAssignee] = useState({
    userName: "",
    userId: "",
    selected: false
  });

  const { data: submittalWorkflowResponses } = useCIQQuery(
    SUBMITTAL_WORKFLOW_RESPONSES,
    {
      client: gqlClientForProject,
      skip: !gqlClientForProject
    }
  );

  const {
    showReviewOption,
    contentClassNames,
    assignDisabledTooltipMsg,
    getAttachBtnDisabledMsg,
    cannotRespondToWorkflow,
    isLoggedInUserMainInWorkflow,
    submittalParticipants,
    showOverrideWorkflowBtn,
    loggedInUserId,
    permission: {
      isLoggedInUserGcAdmin,
      isLoggedInUserGC,
      isLoggedInUserSC,
      isCurrentAssigneeGCUser,
      isLoggedInUserWFGCUser,
      isLoggedInUserOwner,
      isLoggedInUserArchitect,
      isLoggedInUserEngineer
    }
  } = useSubmmittialDetails({
    featureInstanceData: localFeatureInstanceData,
    isRevisionMode,
    submittal,
    editableFields,
    submittalViewState,
    isWorkflowInOverrideMode
  });

  const [reviewVal, setReviewVal] = useState(
    submittalWorkflowResponses?.submittal_response?.length
      ? submittalWorkflowResponses?.submittal_response[0]?.id
      : ""
  );
  const [commentVal, setCommentVal] = useState("");
  const [uploadedDocumentList, setUploadedDocumentList] = useState<
    Array<IDocumentObject>
  >([]);

  const [openDistributeModel, setOpenDistributeSubmittalModel] =
    useState(false);

  const onSelectAltAssign = (assigneeId: string, assigneeUser: string) => {
    setAltAssignee({
      userName: assigneeUser,
      userId: assigneeId,
      selected: true
    });
  };
  const clearAltAssignee = () => {
    setAltAssignee({
      userName: "",
      userId: "",
      selected: false
    });
  };

  const showAdvAssignBtn =
    submittalViewState.status !== ESubmittalStatus.DONE &&
    loggedInUserId === submittalParticipants?.assignee?.user?.id;

  const updateSubmittalSuccessResp = [
    "Updated Successfully",
    "Ball In Court updated"
  ];
  const updateSubmittalErrResp = ["No Updates Allowed", "Update Failed"];

  const getOnBehalfOfInfoObj = () => {
    let onBehalfOfInfoObj: any = null;
    if (isWorkflowInOverrideMode) {
      onBehalfOfInfoObj = {};
      const assignee = localFeatureInstanceData?.feature_user_paticipants?.find(
        (user: any) =>
          user?.user_participation_type?.id === UserParticipationType.ASSIGNEE
      );
      if (assignee) {
        onBehalfOfInfoObj.on_behalf_of_user_id = assignee?.user?.id;
      }
    }
    return onBehalfOfInfoObj;
  };

  const isCommentDisabled =
    !isWorkflowInOverrideMode &&
    (isRevisionMode ||
      cannotRespondToWorkflow ||
      !editableFields.includes("comment") ||
      !submittalParticipants?.gcReviewer?.user?.id ||
      !submittalParticipants?.drReviewer?.user?.id ||
      !submittalParticipants?.submitter?.user?.id);

  const assigneeActualDateStates = useMemo(() => {
    return getAssigneeActualDateStates();
  }, []);

  useEffect(() => {
    const dateBlocks = localFeatureInstanceData?.workflow_instance_milestones;
    if (dateBlocks?.length > 0) {
      const getIndex = () => {
        if (submittal?.workflow_status === ESubmittalStatus.CREATE) return 0;
        if (submittal?.workflow_status === ESubmittalStatus.SUBMIT) return 1;
        if (submittal?.workflow_status === ESubmittalStatus.GC_REVIEW)
          return reviewVal > 4 ? 0 : 2;
        if (submittal?.workflow_status === ESubmittalStatus.EXTERNAL_REVIEW)
          return 3;
        if (
          submittal?.workflow_status === ESubmittalStatus.READY_FOR_DISTRIBUTION
        )
          return reviewVal > 4 ? 0 : 4;
        return 0;
      };

      const currentStateIndex = getIndex();
      const dateBlock: any = dateBlocks[currentStateIndex];

      const previousDate =
        currentStateIndex > 0 ? dateBlock?.actual_date : null;

      setCurrentActualDateState({
        value: previousDate
          ? DateUtils.dateTimeObj(previousDate)
          : DateUtils.dateTimeObj(),
        previousDate,
        ...assigneeActualDateStates[currentStateIndex]
      });
    }
  }, [
    assigneeActualDateStates,
    reviewVal,
    localFeatureInstanceData?.workflow_instance_milestones,
    submittal?.workflow_status
  ]);

  useEffect(() => {
    setIsAltAssigne(false);

    if (loggedInUserId === submittalParticipants?.assignee?.user?.id) {
      if (
        submittal?.workflow_status === ESubmittalStatus.SUBMIT &&
        submittalParticipants?.assignee?.user?.id !==
          submittalParticipants?.submitter?.user?.id
      ) {
        setIsAltAssigne(true);
      }

      if (
        submittal?.workflow_status === ESubmittalStatus.GC_REVIEW &&
        submittalParticipants?.assignee?.user?.id !==
          submittalParticipants?.gcReviewer?.user?.id
      ) {
        setIsAltAssigne(true);
      }

      if (
        submittal?.workflow_status === ESubmittalStatus.EXTERNAL_REVIEW &&
        submittalParticipants?.assignee?.user?.id !==
          submittalParticipants?.drReviewer?.user?.id
      ) {
        setIsAltAssigne(true);
      }

      if (
        submittalViewState?.status ===
          ESubmittalStatus.READY_FOR_DISTRIBUTION &&
        submittalParticipants?.assignee?.user?.id !==
          submittalParticipants?.gcReviewer?.user?.id
      ) {
        setIsAltAssigne(true);
      }
    }
  }, [
    loggedInUserId,
    submittalViewState?.status,
    submittal?.workflow_status,
    submittalParticipants?.assignee?.user?.id,
    submittalParticipants?.drReviewer?.user?.id,
    submittalParticipants?.gcReviewer?.user?.id,
    submittalParticipants?.submitter?.user?.id,
    submittalParticipants,
    submittalViewState
  ]);

  const markAsDone = useMemo(() => {
    return (
      (isLoggedInUserGC &&
        submittalViewState?.status === ESubmittalStatus.REVIEW &&
        submittal?.submittal_status_history_details?.length &&
        submittal?.submittal_status_history_details[0]?.submittal_response_id &&
        submittal?.submittal_status_history_details[0]?.submittal_response_id <=
          4 &&
        submittal?.submittal_status_history_details[1]?.assignee ===
          submittalParticipants.drReviewer?.user?.id &&
        reviewVal <= 4) ||
      false
    );
  }, [
    isLoggedInUserGC,
    reviewVal,
    submittal?.submittal_status_history_details,
    submittalParticipants.drReviewer?.user?.id,
    submittalViewState?.status
  ]);

  const WFResponses = useMemo(() => {
    if (!submittalWorkflowResponses?.submittal_response) return null;

    const responseState = {
      hasDRRejected: false,
      responses: submittalWorkflowResponses?.submittal_response
    };

    if (submittal?.workflow_status === ESubmittalStatus.GC_REVIEW) {
      const latestSubmittalWFHistoryItem: any =
        submittal?.submittal_status_history_details?.find((history: any) => {
          return history?.status !== null;
        });
      const responseId = latestSubmittalWFHistoryItem?.submittal_response_id;

      if (responseId && responseId > 4) {
        responseState.hasDRRejected = true;
        responseState.responses =
          submittalWorkflowResponses?.submittal_response?.filter(
            (WFResponse: any) => {
              return WFResponse.id > 4;
            }
          );
      }
    }
    return responseState;
  }, [submittalWorkflowResponses?.submittal_response, submittal]);

  const overrideWFConfirmDialog = () => {
    modal.confirm({
      content: "Are you sure you want override the workflow?",
      onOk() {
        setIsWorkflowInOverrideMode(true);
      },
      okText: "Confirm"
    });
  };

  useEffect(() => {
    if (WFResponses) {
      setReviewVal(WFResponses.responses[0]?.id);
    }
  }, [WFResponses]);

  const showDrRejectionMsgToGC =
    (isLoggedInUserGcAdmin &&
      isCurrentAssigneeGCUser &&
      isLoggedInUserWFGCUser &&
      WFResponses?.hasDRRejected) ||
    (isLoggedInUserGcAdmin &&
      isCurrentAssigneeGCUser &&
      !isLoggedInUserWFGCUser &&
      isWorkflowInOverrideMode &&
      WFResponses?.hasDRRejected);

  const getNextAssignee = () => {
    if (isLoggedInUserGC) {
      if (
        // when submittal is just created
        submittalViewState?.status === ESubmittalStatus.CREATE
      ) {
        if (!submittalParticipants?.submitter)
          return {
            foundUser: null,
            userNameStr: ""
          };

        if (
          submittalParticipants?.submitter &&
          submittalParticipants?.submitter?.user
        ) {
          return {
            type: "actual",
            foundUser: submittalParticipants?.submitter?.user,
            userNameStr: `${
              submittalParticipants?.submitter?.user?.first_name ?? ""
            } ${submittalParticipants?.submitter?.user?.last_name ?? ""}`
          };
        }
      }
      // when assigned to a submitter
      if (
        submittalViewState?.status === ESubmittalStatus.SUBMIT &&
        submittalParticipants?.gcReviewer?.user
      ) {
        return {
          type: "actual",
          foundUser: submittalParticipants?.gcReviewer?.user,
          userNameStr: `${
            submittalParticipants?.gcReviewer?.user?.first_name ?? ""
          } ${submittalParticipants?.gcReviewer?.user?.last_name ?? ""}`
        };
      }

      // when its assigned to GC User for Review and review status is positive, assign to design reviewer
      if (
        (submittal?.workflow_status === ESubmittalStatus.GC_REVIEW ||
          submittal?.workflow_status ===
            ESubmittalStatus.READY_FOR_DISTRIBUTION) &&
        reviewVal <= 4
      ) {
        if (submittalParticipants?.drReviewer) {
          return {
            type: "actual",
            foundUser: submittalParticipants?.drReviewer?.user,
            userNameStr: `${
              submittalParticipants?.drReviewer?.user?.first_name ?? ""
            } ${submittalParticipants?.drReviewer?.user?.last_name ?? ""}`
          };
        }
      }

      // when its assigned to GC User for Review and review status is negative, assign it back to submitter
      if (
        (submittal?.workflow_status === ESubmittalStatus.GC_REVIEW ||
          submittal?.workflow_status ===
            ESubmittalStatus.READY_FOR_DISTRIBUTION) &&
        reviewVal > 4
      ) {
        if (submittalParticipants?.submitter?.user) {
          return {
            type: "actual",
            foundUser: submittalParticipants?.submitter?.user,
            userNameStr: `${
              submittalParticipants?.submitter?.user?.first_name ?? ""
            } ${submittalParticipants?.submitter?.user?.last_name ?? ""}`
          };
        }
      }

      // when its assigned to DR User for Review and review status can be anything, always assign it GC
      if (
        submittal?.workflow_status === ESubmittalStatus.EXTERNAL_REVIEW &&
        submittalParticipants?.gcReviewer
      ) {
        return {
          foundUser: submittalParticipants?.gcReviewer?.user,
          userNameStr: `${
            submittalParticipants?.gcReviewer?.user?.first_name ?? ""
          } ${submittalParticipants?.gcReviewer?.user?.last_name ?? ""}`
        };
      }
    }

    if (
      (isLoggedInUserSC || isLoggedInUserOwner) &&
      submittalParticipants?.gcReviewer
    ) {
      return {
        type: "actual",
        foundUser: submittalParticipants?.gcReviewer?.user,
        userNameStr: `${
          submittalParticipants?.gcReviewer?.user?.first_name ?? ""
        } ${submittalParticipants?.gcReviewer?.user?.last_name ?? ""}`
      };
    }

    if (
      (isLoggedInUserArchitect || isLoggedInUserEngineer) &&
      submittalParticipants?.gcReviewer
    ) {
      return {
        type: "actual",
        foundUser: submittalParticipants?.gcReviewer?.user,
        userNameStr: `${
          submittalParticipants?.gcReviewer?.user?.first_name ?? ""
        } ${submittalParticipants?.gcReviewer?.user?.last_name ?? ""}`
      };
    }

    return {
      foundUser: null,
      userNameStr: ""
    };
  };

  const actionButtonLabel = () => {
    if (submittal?.workflow_status === ESubmittalStatus.CREATE) {
      return "Assign";
    }
    if (submittal?.workflow_status === ESubmittalStatus.SUBMIT) {
      return altAssignee.selected
        ? "Assigne"
        : reviewVal > 4
        ? "Return"
        : "Submit";
    }
    if (submittal?.workflow_status === ESubmittalStatus.GC_REVIEW) {
      return altAssignee.selected
        ? "Assigne"
        : reviewVal > 4
        ? "Return"
        : "Submit for Review";
    }
    if (submittal?.workflow_status === ESubmittalStatus.EXTERNAL_REVIEW) {
      return altAssignee.selected
        ? "Assigne"
        : reviewVal > 4
        ? "Return"
        : "Submit Review";
    }
    if (
      submittal?.workflow_status === ESubmittalStatus.READY_FOR_DISTRIBUTION
    ) {
      return altAssignee.selected
        ? "Assigne"
        : reviewVal > 4
        ? "Return"
        : "Close and Distribute";
    }
    return "Confirm";
  };

  const getAssigneeRow = () => {
    if (!altAssignee.selected) {
      return (
        <div className="flex items-center justify-between mb-3">
          <div className="review-action">
            {markAsDone ? (
              <b>Close and Distribute</b>
            ) : isAltAssignee ? (
              ""
            ) : (
              <span>
                <b>Assign To:</b> {getNextAssignee().userNameStr || "Submitter"}
              </span>
            )}
          </div>
          {showOverrideWorkflowBtn && !isWorkflowInOverrideMode && (
            <div>
              <Button
                onClick={overrideWFConfirmDialog}
                disabled={isRevisionMode}
              >
                Override Workflow
              </Button>
            </div>
          )}
          {submittalViewState?.status !== ESubmittalStatus.DONE &&
            showAdvAssignBtn && (
              <>
                {!isAltAssignee ? <b>OR</b> : ""}
                <BtnAlternateAssign
                  disabled={
                    isRevisionMode ||
                    cannotRespondToWorkflow ||
                    submittalAssignInProg
                  }
                  tooltip={assignDisabledTooltipMsg}
                  projectId={projectId}
                  submittalData={{
                    gc_reviewer: submittalParticipants?.gcReviewer?.user?.id,
                    submitter: submittalParticipants?.submitter?.user?.id,
                    design_reviewer: submittalParticipants?.drReviewer?.user?.id
                  }}
                  onAssigneeSelect={onSelectAltAssign}
                  loading={submittalAssignInProg}
                />
              </>
            )}
        </div>
      );
    }
    return (
      <div className="alt-assignee-chip flex items-center justify-between py-1 px-3 mb-3 border-solid border border-neutral-200">
        <span>
          <b>Assign To: </b>
          {altAssignee.userName}
        </span>
        <CloseCircleOutlined onClick={clearAltAssignee} />
      </div>
    );
  };

  const showActionButton = () => {
    return (
      submittal?.workflow_status === ESubmittalStatus.CREATE ||
      isWorkflowInOverrideMode ||
      submittalParticipants?.assignee?.user?.id === loggedInUserId
    );
  };

  const getLatestWFHistoryItem = (historyItems: any) => {
    if (!historyItems) return null;
    const historyArr = historyItems.filter(
      (historyItem: any) =>
        !(
          historyItem.action &&
          historyItem.action.toLowerCase().includes("deleted")
        )
    );
    if (historyArr && historyArr?.length > 0) {
      return historyArr[0];
    }
    return null;
  };

  const UploadAttachment = async ({ file, onSuccess }: any) => {
    let requestPayload = {
      feature: "SUBMITTALS",
      feature_id: localFeatureInstanceData?.id,
      file_name: file.name
    };

    const onBehalfOfInfo = getOnBehalfOfInfoObj();
    if (onBehalfOfInfo) {
      requestPayload = { ...requestPayload, ...onBehalfOfInfo };
    }

    const historyItem = getLatestWFHistoryItem(
      submittal?.submittal_status_history_details || []
    );

    const subHistoryObj: any = {};
    if (historyItem) {
      subHistoryObj.submittal_status_history_id = `${historyItem.id}`;
      requestPayload = {
        ...requestPayload,
        ...subHistoryObj
      };
    }
    const signedUrlRespose: any = await generateS3URL(
      requestPayload,
      tokenRetrievalState.token
    );

    if (signedUrlRespose.error) {
      message.error(signedUrlRespose.error.msg);
      return null;
    }

    const fileUploadResponse: any = await noAuthClient.put(
      `${signedUrlRespose.data.success.url}`,
      file,
      {
        onUploadProgress: (progressEvent) => {
          const progressCount = Math.ceil(
            (progressEvent.loaded / progressEvent.total) * 100
          );
          console.log(progressCount);
          // if (setProgress) setProgress(progressCount);
        }
      }
    );
    if (fileUploadResponse.error) {
      message.error(signedUrlRespose.error.msg);
      return null;
      // reject(new Error(fileUploadResponse.error));
    }
    onSuccess("OK");
    return fileUploadResponse;
  };

  const assignSubmittal = async () => {
    setSubmittalAssignInProg(true);

    const nextAssignee = getNextAssignee();

    const nextAssigneeUser = nextAssignee.foundUser;

    const updateSubmittalValues: any = {
      feature_instance_id: localFeatureInstanceData?.id,
      submittal_response_id: null,
      on_behalf_of: null,
      comment: ""
    };

    updateSubmittalValues.assign_to =
      nextAssigneeUser.user_id || nextAssigneeUser.id;

    if (commentVal.trim() !== "") {
      updateSubmittalValues.comment = commentVal.trim();
    }

    if (showReviewOption) {
      updateSubmittalValues.submittal_response_id = reviewVal;
    }

    if (isWorkflowInOverrideMode) {
      if (submittalParticipants?.assignee) {
        updateSubmittalValues.on_behalf_of =
          submittalParticipants?.assignee?.user?.id;
      }
    }

    if (isLoggedInUserGcAdmin) {
      updateSubmittalValues.actual_milestone_date =
        DateUtils.formatDateWithLunchTime(currentActualDateState.value);
    }

    const payload = {
      variables: {
        feature_instance_type_id: FeatureTypes.SUBMITTALS,
        feature_instances: [updateSubmittalValues]
      }
    };

    try {
      const updateResponse = await actions.updateFeatureInstancesResponse(
        payload
      );
      const cloneOfUploadedDocumentList = uploadedDocumentList.map(
        (docs: IDocumentObject) => ({
          file: docs.file,
          status: docs.status
        })
      );
      // eslint-disable-next-line no-restricted-syntax
      for (const index of Array.from(
        Array(uploadedDocumentList.length).keys()
      )) {
        cloneOfUploadedDocumentList[index].status = 2; // Loading state
        setUploadedDocumentList([...cloneOfUploadedDocumentList]);
        try {
          // eslint-disable-next-line no-await-in-loop
          await UploadAttachment({
            file: uploadedDocumentList[index].file,
            onSuccess: () => {}
          });
          cloneOfUploadedDocumentList[index].status = 3; // Success State
          setUploadedDocumentList([...cloneOfUploadedDocumentList]);
        } catch {
          cloneOfUploadedDocumentList[index].status = 4; // Fail State
          setUploadedDocumentList([...cloneOfUploadedDocumentList]);
        }
      }
      setUploadedDocumentList([]);
      setSubmittalAssignInProg(false);

      const updateResponseMsg =
        updateResponse?.data?.update_feature_instances?.message;

      if (updateSubmittalSuccessResp.includes(updateResponseMsg)) {
        message.success(updateResponseMsg);
        setIsWorkflowInOverrideMode(false);
      }

      if (updateSubmittalErrResp.includes(updateResponseMsg)) {
        modal.error({ content: updateResponseMsg });
        return;
      }
    } catch (ex) {
      console.log(ex);
      modal.error({ content: "An error occured" });
      setSubmittalAssignInProg(false);
      return;
    }
    setCommentVal("");
    setUploadedDocumentList([]);
  };

  const alternateAssignSubmittal = async (assigneeId: string) => {
    const postObj: any = {
      feature_instance_id: localFeatureInstanceData?.id,
      actual_milestone_date: null,
      submittal_response_id: null,
      on_behalf_of: null,
      comment: "",
      assignee: assigneeId
    };

    const comment = commentVal.trim();
    if (comment !== "") {
      postObj.comment = comment;
    }

    setSubmittalAssignInProg(true);
    try {
      const cloneOfUploadedDocumentList = uploadedDocumentList.map(
        (docs: IDocumentObject) => ({
          file: docs.file,
          status: docs.status
        })
      );
      // eslint-disable-next-line no-restricted-syntax
      for (const index of Array.from(
        Array(uploadedDocumentList.length).keys()
      )) {
        cloneOfUploadedDocumentList[index].status = 2; // Loading state
        setUploadedDocumentList([...cloneOfUploadedDocumentList]);
        try {
          // eslint-disable-next-line no-await-in-loop
          await UploadAttachment({
            file: uploadedDocumentList[index].file,
            onSuccess: () => {}
          });
          cloneOfUploadedDocumentList[index].status = 3; // Success State
          setUploadedDocumentList([...cloneOfUploadedDocumentList]);
        } catch {
          cloneOfUploadedDocumentList[index].status = 4; // Fail State
          setUploadedDocumentList([...cloneOfUploadedDocumentList]);
        }
      }
      setUploadedDocumentList([]);

      const payload = {
        variables: {
          feature_instance_type_id: FeatureTypes.SUBMITTALS,
          feature_instances: [postObj]
        }
      };

      const updateResponse = await actions.updateFeatureInstancesResponse(
        payload
      );
      const updateResponseMsg =
        updateResponse.data.update_feature_instances.message;

      if (updateSubmittalSuccessResp.includes(updateResponseMsg)) {
        message.success(updateResponseMsg);
        setCommentVal("");
      }

      if (updateSubmittalErrResp.includes(updateResponseMsg)) {
        modal.error({ content: updateResponseMsg });
        return;
      }
    } catch (ex: any) {
      modal.error({ content: ex.message });
    }
    clearAltAssignee();
    setSubmittalAssignInProg(false);
  };

  return (
    <div className="" data-testid="swr-root">
      <Form layout="vertical" data-testid="swr-form">
        <Tooltip
          title={
            isRevisionMode
              ? "Go to the latest revision to respond to the workflow."
              : ""
          }
          arrowPointAtCenter
        >
          <div
            className={`overflow-auto ${contentClassNames || ""}`}
            data-testid="swr-content"
          >
            <div>
              {showReviewOption && (
                <Tooltip
                  title={assignDisabledTooltipMsg}
                  placement="rightBottom"
                >
                  <Form.Item label="Response" className="!leading-none">
                    <Select
                      value={reviewVal}
                      onChange={(val) => {
                        setReviewVal(val);
                      }}
                      disabled={
                        !isWorkflowInOverrideMode &&
                        (isRevisionMode ||
                          cannotRespondToWorkflow ||
                          !editableFields.includes("review_status"))
                      }
                      data-testid="swr-select-response"
                    >
                      {WFResponses?.responses?.map((response: any) => {
                        return (
                          <Option key={response.id} value={response.id}>
                            {response.response}
                          </Option>
                        );
                      })}
                    </Select>
                    {showDrRejectionMsgToGC && (
                      <span className="text-xs text-amber-600">
                        The submittal has been rejected by the Design Reviewer.
                        You have to reject and reassign the submittal to the
                        subcontractor.
                      </span>
                    )}
                  </Form.Item>
                </Tooltip>
              )}

              <div>
                <Tooltip
                  title={assignDisabledTooltipMsg}
                  placement="rightBottom"
                >
                  <Form.Item label="Comment">
                    <TextArea
                      rows={2}
                      value={commentVal}
                      onChange={(event) => {
                        setCommentVal(event.target.value);
                      }}
                      disabled={isCommentDisabled}
                      data-testid="swr-textarea-comment"
                    />
                  </Form.Item>
                </Tooltip>
              </div>
              <div>
                {uploadedDocumentList.map(
                  (docs: IDocumentObject, index: number) => {
                    return (
                      <div
                        className="border-solid border border-neutral-200 workflow-document-prereview"
                        data-testid="swr-uploaded-item"
                      >
                        {docs.status === 2 ? (
                          <LoadingOutlined className="mr-2" />
                        ) : docs.status === 3 ? (
                          <CheckCircleFilled className="mr-2" />
                        ) : (
                          <LinkOutlined className="mr-2" />
                        )}
                        <div className="workflow-document-prereview--title truncate">
                          {docs.file.name}
                        </div>
                        <DeleteOutlined
                          onClick={() => {
                            setUploadedDocumentList(
                              uploadedDocumentList.filter(
                                (_: IDocumentObject, itemIndex: number) =>
                                  itemIndex !== index
                              )
                            );
                          }}
                          data-testid="swr-button-remove-uploaded"
                        />
                      </div>
                    );
                  }
                )}
              </div>
              {isLoggedInUserGcAdmin && !isCommentDisabled && (
                <div className="mb-1 flex py-1 space-x-1">
                  <div>{currentActualDateState?.label}</div>
                  <div className="font-semibold">
                    {DateUtils.format(currentActualDateState?.value)}
                  </div>
                  <DatePickerWithIcon
                    value={currentActualDateState?.value}
                    disabledDate={(current) => {
                      if (currentActualDateState?.previousDate) {
                        const previousDate = DateUtils.dateTimeObj(
                          currentActualDateState?.previousDate
                        );
                        return previousDate.isAfter(current);
                      }
                      return false;
                    }}
                    onChange={(val) => {
                      if (val)
                        setCurrentActualDateState((prev) => ({
                          ...prev,
                          value: val
                        }));
                    }}
                    data-testid="swr-datepicker-actual"
                  />
                </div>
              )}
              <div className="mb-2 flex justify-end">
                <Upload
                  showUploadList={false}
                  multiple
                  customRequest={({ file }: any) => {
                    setUploadedDocumentList([
                      ...uploadedDocumentList,
                      {
                        file,
                        status: 1
                      }
                    ]);
                  }}
                  data-testid="swr-upload"
                >
                  {isAltAssignee ? (
                    <Button
                      size="small"
                      type="text"
                      className="workflow-document-upload"
                      disabled={isRevisionMode}
                      data-testid="swr-button-add-attachment-alt"
                    >
                      Add Attachment
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      type="text"
                      className="workflow-document-upload"
                      title={getAttachBtnDisabledMsg()}
                      disabled={
                        !isWorkflowInOverrideMode &&
                        (submittalAssignInProg ||
                          isRevisionMode ||
                          (submittalViewState?.status !==
                            ESubmittalStatus.CREATE &&
                            !isLoggedInUserMainInWorkflow) ||
                          cannotRespondToWorkflow ||
                          !editableFields.includes("assign") ||
                          !submittalParticipants?.gcReviewer?.user?.id ||
                          !submittalParticipants?.drReviewer?.user?.id ||
                          !submittalParticipants?.submitter?.user?.id)
                      }
                      data-testid="swr-button-add-attachment"
                    >
                      Add Attachment
                    </Button>
                  )}
                </Upload>
              </div>
              {!(
                submittalViewState?.status === ESubmittalStatus.DONE ||
                (submittalViewState?.status === ESubmittalStatus.CREATE &&
                  !submittalParticipants?.drReviewer?.user?.id)
              ) ? (
                <>
                  {getAssigneeRow()}
                  {showActionButton() ? (
                    submittalViewState?.status !== ESubmittalStatus.DONE ? (
                      <div className="flex justify-end">
                        {markAsDone ? (
                          <>
                            <Button
                              type="primary"
                              onClick={() => {
                                setOpenDistributeSubmittalModel(true);
                              }}
                              disabled={
                                !isWorkflowInOverrideMode &&
                                (loggedInUserId !==
                                  submittalParticipants?.gcReviewer?.user?.id ||
                                  submittalAssignInProg)
                              }
                            >
                              {actionButtonLabel()}
                            </Button>
                            <Modal
                              title="Submittal Summary"
                              width={500}
                              open={openDistributeModel}
                              onOk={() => {
                                setOpenDistributeSubmittalModel(false);
                                assignSubmittal();
                              }}
                              onCancel={() => {
                                setOpenDistributeSubmittalModel(false);
                              }}
                              okText="Close and Distribute"
                              destroyOnClose
                              data-testid="swr-modal-distribute"
                            >
                              <CloseAndDistributeSubmittal
                                submittalData={localFeatureInstanceData}
                                submittalWorkflowResponses={
                                  submittalWorkflowResponses?.submittal_response
                                }
                                reviewVal={reviewVal}
                                submittalViewState={{
                                  status: submittalViewState?.status,
                                  userType: ""
                                }}
                                commentVal={commentVal}
                                data-testid="swr-distribute-component"
                              />
                            </Modal>
                          </>
                        ) : isAltAssignee ? (
                          <Button
                            type="primary"
                            onClick={() => {
                              if (altAssignee.selected) {
                                alternateAssignSubmittal(altAssignee.userId);
                              } else {
                                assignSubmittal();
                              }
                            }}
                            disabled={!altAssignee.selected}
                            loading={submittalAssignInProg}
                            title={assignDisabledTooltipMsg}
                            data-testid="swr-button-action-alt"
                          >
                            {actionButtonLabel()}
                          </Button>
                        ) : (
                          <Button
                            type="primary"
                            onClick={() => {
                              if (altAssignee.selected) {
                                alternateAssignSubmittal(altAssignee.userId);
                              } else {
                                assignSubmittal();
                              }
                            }}
                            disabled={
                              !isWorkflowInOverrideMode &&
                              (isRevisionMode ||
                                (submittalViewState?.status !==
                                  ESubmittalStatus.CREATE &&
                                  !isLoggedInUserMainInWorkflow) ||
                                cannotRespondToWorkflow ||
                                !editableFields.includes("assign") ||
                                !submittalParticipants?.gcReviewer?.user?.id ||
                                !submittalParticipants?.drReviewer?.user?.id ||
                                !submittalParticipants?.submitter?.user?.id ||
                                submittalAssignInProg)
                            }
                            loading={submittalAssignInProg}
                            title={assignDisabledTooltipMsg}
                            data-testid="swr-button-action"
                          >
                            {actionButtonLabel()}
                          </Button>
                        )}
                      </div>
                    ) : (
                      ""
                    )
                  ) : (
                    ""
                  )}
                </>
              ) : (
                ""
              )}
            </div>
          </div>
          {contextHolder}
        </Tooltip>
      </Form>
    </div>
  );
}
